import {
  Box,
  Button as MuiButton,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ButtonVariantOption } from "../../components/Button";
import { PagePaper, SectionPaper } from "../../components/Paper";
import { FeedGrid } from "./components/FeedGrid";
import { ConfirmAnalyzeDialog } from "./components/ConfirmAnalyzeDialog";
import TopicIcon from "@mui/icons-material/Topic";
import DownloadIcon from "@mui/icons-material/Download";
import React, { useState } from "react";
import { useCommercialBookStatus } from "./hooks/useCommercialBookStatus";
import { useCommercialZipDownloadUrl } from "./api/getZipDownloadUrl";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { KeyboardArrowLeft } from "@mui/icons-material";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useApiClient } from "@/hooks/useApiClient";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { PostCommercialPictureRequestAnalyzeAPI } from "@/features/commercialPictureAnalysis/api/postCommercialPictureRequestAnalyzeAPI";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { HttpStatusCode, isAxiosError } from "axios";
import { type HandOffMessage } from "@/types/handOffMessage";
import {
  acquireBookIsDone,
  getStatusString as getBookStatusString,
} from "@/utils/utilsBookStatus";

export const CommercialBookListPresenter: React.FC = () => {
  // NOTE: commercialBookZipDownloadは、BEのリリースと遡及生成の完了後にONにする
  const { commercialBookZipDownload, displayCheckboxCommercialPictureAnalyze } =
    useFeatureFlags();
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiClient } = useApiClient();
  const isReadOnly = useReadOnlyUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // SP（カード表示）では外側の白カードを付けない（カードinカードを避ける）。PCは従来どおりSectionPaper。
  const ResultsContainer = isMobile ? Box : SectionPaper;

  const { data } = useCommercialBookStatus(id);
  const acStatusMessage = getBookStatusString(data);
  const { handleDownload } = useCommercialZipDownloadUrl();
  // ZIPには取得に成功した明細だけが入るため、成功が0件なら中身が空になる。
  // 明細0件（取得完了と判定されてしまう）と全件失敗（acquireBookIsDoneが
  // 「失敗あり」もtrueにする）の両方がここに該当する。
  // NOTE: 読み込み中（data === undefined）はeveryの結果がundefinedになり、
  //       === true により成功0件とは区別される
  const hasNoSuccessDetail =
    data?.list.every((detail) => detail.status !== "SUCCEEDED") === true;
  // NOTE: ZIPは全明細の取得完了後に作成されるため、取得中はダウンロードさせない
  const isDone = acStatusMessage !== "" && acquireBookIsDone(acStatusMessage);
  const isDownloadable = isDone && !hasNoSuccessDetail;
  // 取得中はまだ成功明細が0件でも「成功なし」とは言えないため、取得完了の判定を先に置く
  const downloadDisabledReason = !isDone
    ? "取得完了後に一括ダウンロードが可能になります。"
    : "取得に成功した登記がないためダウンロードできません。";

  // 後追い解析の選択状態
  const [selectedAnalyzeTargets, setSelectedAnalyzeTargets] = useState<
    Map<number, boolean>
  >(new Map());
  const [openAnalyzeDialog, setOpenAnalyzeDialog] = useState<boolean>(false);
  const hasAnalyzeTargets = [...selectedAnalyzeTargets.values()].some(Boolean);
  const alreadyRequestedAnalyze = (data?.list ?? []).some(
    (item) => item.commercialPictureAnalyzeRequestId !== null
  );

  // 解析対象として選択可能な明細ID（成功かつ未解析）。SPの操作バーの全選択に使う
  const selectableAnalyzeIds = (data?.list ?? [])
    .filter(
      (item) =>
        item.status === "SUCCEEDED" &&
        item.commercialPictureAnalyzeRequestId === null
    )
    .map((item) => item.id);
  const allAnalyzeSelected =
    selectableAnalyzeIds.length > 0 &&
    selectableAnalyzeIds.every((id) => selectedAnalyzeTargets.get(id) === true);
  const toggleAllAnalyze = (checked: boolean): void => {
    const next = new Map(selectedAnalyzeTargets);
    selectableAnalyzeIds.forEach((id) => next.set(id, checked));
    setSelectedAnalyzeTargets(next);
  };
  // 解析UI（チェック＋ボタン）を表示するか
  const showAnalyzeUi =
    Boolean(displayCheckboxCommercialPictureAnalyze) &&
    !alreadyRequestedAnalyze &&
    data?.canModify !== false;

  const handleAnalyzeButtonClick = (cancelLoading: () => void): void => {
    if (data?.canModify === false) {
      cancelLoading();
      return;
    }
    if (isReadOnly) {
      cancelLoading();
      toast.error(READ_ONLY_TOOLTIP_MESSAGE);
      return;
    }

    // ダイアログを開いた後にSWRの再取得で解析済みに変わっている可能性があるため、
    // ボタンのdisabled判定（レンダリング時点）とは別に、送信直前にも再チェックする。
    if (alreadyRequestedAnalyze) {
      cancelLoading();
      toast.error("既に解析リクエスト済みです");
      return;
    }

    const detailIds = [...selectedAnalyzeTargets.entries()]
      .filter(([, checked]) => checked)
      .map(([detailId]) => detailId);

    // ボタンのdisabled判定（hasAnalyzeTargets、レンダリング時点）とは別に、
    // ハンドラ単体で呼ばれた場合にも空配列でAPIを呼ばないよう送信直前にも防御する。
    if (detailIds.length === 0) {
      cancelLoading();
      return;
    }

    PostCommercialPictureRequestAnalyzeAPI(apiClient, { detailIds }).catch(
      (e) => {
        Sentry.captureException(e);
        cancelLoading();
        if (
          isAxiosError(e) &&
          e.response?.status === HttpStatusCode.Forbidden
        ) {
          toast.error(READ_ONLY_TOOLTIP_MESSAGE);
          return;
        }
        toast.error("商業登記解析の実行に失敗しました");
      }
    );

    // 上のPostCommercialPictureRequestAnalyzeAPI()はawaitしてないしthen()も無いため、
    // 成功しても失敗してもすぐに下のコードが実行される
    const handOffMessage: HandOffMessage = {
      command: "COMMERCIAL_PICTURE_ANALYSIS_SHOW_ACCEPT_MESSAGE",
    };

    // 成功した場合は次の画面へ遷移するのでcancelLoading()の呼び出しは不要
    navigate("/commercialpictureanalysis/list", {
      replace: true,
      state: { handOffMessage },
    });
  };

  return (
    <PagePaper>
      <form>
        <Box sx={{ display: "flex" }}>
          {/* ページタイトル（SPでは小さめに） */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <TopicIcon sx={{ mr: 1, fontSize: { xs: "1.5rem", sm: "2rem" } }} />
            <Typography
              sx={{
                fontSize: { xs: "1.125rem", sm: "1.5rem" },
                fontWeight: 700,
              }}
            >
              取得済商業登記の詳細を確認する
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Typography
            fontWeight={"bold"}
            sx={{
              color: "#4A86DF",
              fontSize: { xs: "0.95rem", sm: "1.25rem" },
            }}
          >
            ステータス: {acStatusMessage}
          </Typography>
          {/* PCはステータス行の右に解析ボタンを置く。SPは下の操作バーへ */}
          {!isMobile && showAnalyzeUi && (
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <MuiButton
                variant="outlined"
                size="medium"
                disabled={
                  isReadOnly || !hasAnalyzeTargets || alreadyRequestedAnalyze
                }
                onClick={() => {
                  setOpenAnalyzeDialog(true);
                }}
              >
                商業登記を解析する
              </MuiButton>
            </ReadOnlyTooltip>
          )}
        </Box>
        {/* SP：全選択チェックと解析ボタンを横並びの操作バーにまとめる */}
        {isMobile && showAnalyzeUi && selectableAnalyzeIds.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={allAnalyzeSelected}
                  onChange={(e) => {
                    toggleAllAnalyze(e.target.checked);
                  }}
                />
              }
              label={<Typography variant="body2">すべて選択</Typography>}
            />
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <MuiButton
                variant="outlined"
                size="small"
                disabled={
                  isReadOnly || !hasAnalyzeTargets || alreadyRequestedAnalyze
                }
                onClick={() => {
                  setOpenAnalyzeDialog(true);
                }}
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                商業登記を解析する
              </MuiButton>
            </ReadOnlyTooltip>
          </Box>
        )}
        <ResultsContainer>
          <FeedGrid
            row={data?.list}
            canModify={data?.canModify !== false}
            selectedAnalyzeTargets={selectedAnalyzeTargets}
            setSelectedAnalyzeTargets={setSelectedAnalyzeTargets}
          />
          <Grid item xs my={5}>
            <Stack direction="row" justifyContent="center" spacing={2}>
              {commercialBookZipDownload === true &&
              data?.canModify !== false ? (
                <Tooltip title={isDownloadable ? "" : downloadDisabledReason}>
                  <span>
                    <MuiButton
                      variant={ButtonVariantOption.Outlined}
                      color="brand"
                      size="large"
                      startIcon={<DownloadIcon />}
                      disabled={!isDownloadable}
                      onClick={() => {
                        if (id === undefined) return;
                        void handleDownload(id);
                      }}
                    >
                      一括ダウンロード
                    </MuiButton>
                  </span>
                </Tooltip>
              ) : null}
              <MuiButton
                sx={{ display: "none" }} // 完成してないので非表示
                variant="contained"
                size="large"
                onClick={() => {
                  console.log("再取得する");
                }}
              >
                再取得する
              </MuiButton>
            </Stack>
          </Grid>
        </ResultsContainer>

        {/* 戻る（取得済商業登記の一覧へ。詳細はこの一覧から遷移してくるため） */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={"/commercialbook/list"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>取得済商業登記の一覧に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </form>
      {displayCheckboxCommercialPictureAnalyze && data?.canModify !== false && (
        <ConfirmAnalyzeDialog
          open={openAnalyzeDialog}
          handleClose={() => {
            setOpenAnalyzeDialog(false);
          }}
          handleSubmit={handleAnalyzeButtonClick}
          isReadOnly={isReadOnly}
        />
      )}
    </PagePaper>
  );
};
